<!--
  Issue and pull request links in the rendered comments under `root` (the
  description and the conversation): each says its state beside its number
  (`data-ref-state`, shown by CSS, from the Links card's details), and
  hovering or focusing one opens a peek at it: its title, author, branches
  and size, and its description, asked for only then. The peek stays open
  while the pointer is over it (it scrolls, and its links work); moving
  away, Escape, or scrolling the page closes it.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import { anchorRef, type LinkContext, type Open } from '../logic/links';
  import type { RefRequest } from '../logic/refDetails';
  import { reviewDecisionText, type RefPeek } from '../logic/refPeek';
  import { loadRefDetails, refDetail } from '../state/refDetails';
  import { loadRefPeek } from '../state/refPeek';
  import { relTime } from '../logic/time';
  import { carryFilter } from '../logic/filefilter';
  import { nav } from '../nav.svelte';
  import Html from './Html.svelte';

  interface Props {
    root: HTMLElement | null | undefined;
    ctx: LinkContext;
  }
  let { root, ctx }: Props = $props();

  /** How long the pointer rests on a link before its peek opens, and how long after it leaves (link and peek) the peek closes. */
  const OPEN_MS = 350;
  const CLOSE_MS = 200;
  const ID = 'ref-peek';

  /** Marks a link with its state, when the details are in. */
  function paint(a: HTMLElement, key: string) {
    const d = refDetail(key);
    if (d && d.kind !== 'commit') a.dataset.refState = d.state;
  }

  function decorate(el: HTMLElement) {
    const found = new Map<string, RefRequest>();
    const links: [HTMLElement, string][] = [];
    for (const a of el.querySelectorAll<HTMLAnchorElement>('.comment-body a[href]')) {
      if (a.closest('.ref-peek')) continue;
      const r = anchorRef(a, ctx);
      if (!r) continue;
      a.dataset.refKey = r.key;
      links.push([a, r.key]);
      found.set(r.key, { key: r.key, kind: 'ref', owner: r.target.owner, repo: r.target.repo, number: r.target.number });
      paint(a, r.key);
    }
    if (found.size) void loadRefDetails([...found.values()]).then(() => links.forEach(([a, key]) => paint(a, key)));
  }

  // Again whenever what is rendered under `root` changes (a comment arrives, the description tab is chosen).
  $effect(() => {
    const el = root;
    if (!el) return;
    void ctx;
    decorate(el);
    let frame = 0;
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => decorate(el));
    });
    mo.observe(el, { childList: true, subtree: true });
    return () => {
      cancelAnimationFrame(frame);
      mo.disconnect();
    };
  });

  type Shown = {
    key: string;
    anchor: HTMLElement;
    rect: DOMRect;
    label: string;
    open: Open;
    href: string;
    peek: RefPeek | null | undefined;
    failed: boolean;
  };
  let shown: Shown | null = $state(null);
  let card: HTMLElement | null = $state(null);
  let openTimer: ReturnType<typeof setTimeout> | null = null;
  let closeTimer: ReturnType<typeof setTimeout> | null = null;

  const clearTimers = () => {
    if (openTimer) clearTimeout(openTimer);
    if (closeTimer) clearTimeout(closeTimer);
    openTimer = closeTimer = null;
  };

  function show(a: HTMLElement) {
    clearTimers();
    if (shown?.anchor === a) return;
    const r = anchorRef(a, ctx);
    if (!r) return;
    hide();
    const t = r.target;
    const label = t.owner.toLowerCase() === ctx.pr.owner.toLowerCase() && t.repo.toLowerCase() === ctx.pr.repo.toLowerCase() ? `#${t.number}` : `${t.owner}/${t.repo}#${t.number}`;
    const s: Shown = { key: r.key, anchor: a, rect: a.getBoundingClientRect(), label, open: r.open, href: a.getAttribute('href') ?? '', peek: undefined, failed: false };
    shown = s;
    a.setAttribute('aria-describedby', ID);
    loadRefPeek(r.key, t).then(
      (peek) => shown?.anchor === a && (shown.peek = peek),
      () => shown?.anchor === a && (shown.failed = true),
    );
  }

  function hide() {
    clearTimers();
    shown?.anchor.removeAttribute('aria-describedby');
    shown = null;
  }

  const later = (fn: () => void, ms: number) => setTimeout(fn, ms);
  const linkOf = (t: EventTarget | null) => (t instanceof Element ? t.closest<HTMLElement>('.comment-body a[data-ref-key]') : null);

  $effect(() => {
    const el = root;
    if (!el) return;
    const over = (e: PointerEvent) => {
      const a = linkOf(e.target);
      if (!a || e.pointerType === 'touch') return;
      if (shown?.anchor === a) return clearTimers();
      if (openTimer) clearTimeout(openTimer);
      openTimer = later(() => show(a), OPEN_MS);
    };
    const out = (e: PointerEvent) => {
      const a = linkOf(e.target);
      if (!a || (e.relatedTarget instanceof Node && a.contains(e.relatedTarget))) return;
      if (openTimer && shown?.anchor !== a) {
        clearTimeout(openTimer);
        openTimer = null;
      }
      if (shown?.anchor === a) closeTimer = later(hide, CLOSE_MS);
    };
    const focusin = (e: FocusEvent) => {
      const a = linkOf(e.target);
      if (a && a.matches(':focus-visible')) show(a);
    };
    const focusout = (e: FocusEvent) => {
      if (linkOf(e.target) && shown && !(e.relatedTarget instanceof Node && card?.contains(e.relatedTarget))) hide();
    };
    el.addEventListener('pointerover', over);
    el.addEventListener('pointerout', out);
    el.addEventListener('focusin', focusin);
    el.addEventListener('focusout', focusout);
    return () => {
      el.removeEventListener('pointerover', over);
      el.removeEventListener('pointerout', out);
      el.removeEventListener('focusin', focusin);
      el.removeEventListener('focusout', focusout);
    };
  });

  onMount(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && shown) {
        e.stopPropagation();
        hide();
      }
    };
    // The page scrolling moves the link from under the peek; the peek's own scrolling does not.
    const scroll = (e: Event) => shown && !(e.target instanceof Node && card?.contains(e.target)) && hide();
    addEventListener('keydown', key, true);
    addEventListener('scroll', scroll, true);
    return () => {
      removeEventListener('keydown', key, true);
      removeEventListener('scroll', scroll, true);
      clearTimers();
    };
  });

  const pos = $derived.by(() => {
    if (!shown) return { left: 0, top: 0 };
    const a = shown.rect;
    const h = card?.offsetHeight ?? 0;
    const w = card?.offsetWidth ?? 0;
    const left = Math.max(16, Math.min(a.left, innerWidth - w - 16));
    const below = a.bottom + 6;
    const top = below + h > innerHeight - 8 && a.top - 6 - h > 8 ? a.top - 6 - h : below;
    return { left, top };
  });
  const appHref = (o: Open) => (o.kind === 'app' ? carryFilter(o.path, nav.query) : null);
</script>

{#if shown}
  {@const p = shown.peek}
  {@const here = appHref(shown.open)}
  <div
    class="ref-peek"
    id={ID}
    role="tooltip"
    bind:this={card}
    style:left="{pos.left}px"
    style:top="{pos.top}px"
    onpointerenter={() => clearTimers()}
    onpointerleave={() => (closeTimer = later(hide, CLOSE_MS))}
  >
    <div class="ref-peek-head">
      {#if p}<span class="ref-peek-state {p.state}">{p.kind === 'pull' ? 'PR' : 'issue'} · {p.state}</span>{/if}
      <span class="mono muted">{shown.label}</span>
      {#if p}<b class="ref-peek-title">{p.title}</b>{/if}
    </div>
    {#if shown.failed}
      <p class="ref-peek-note muted">Could not load it from GitHub.</p>
    {:else if p === undefined}
      <p class="ref-peek-note muted">Loading…</p>
    {:else if p === null}
      <p class="ref-peek-note muted">GitHub shows nothing here: it may not exist, or you may not have access to it.</p>
    {:else}
      <div class="ref-peek-meta muted">
        <span>{p.author ?? 'ghost'} opened {relTime(p.createdAt)}</span>
        {#if p.pull}
          <span class="mono">{p.pull.base} ← {p.pull.head}</span>
          <span><b class="add">+{p.pull.additions}</b> <b class="del">−{p.pull.deletions}</b> in {p.pull.changedFiles} file{p.pull.changedFiles === 1 ? '' : 's'}</span>
          {#if reviewDecisionText(p.pull.reviewDecision)}<span>{reviewDecisionText(p.pull.reviewDecision)}</span>{/if}
        {/if}
        <span>{p.comments} comment{p.comments === 1 ? '' : 's'}</span>
      </div>
      {#if p.body.trim() || p.bodyHTML.trim()}
        <div class="ref-peek-body"><Html html={p.bodyHTML} md={p.body} class="comment-body markdown" /></div>
      {:else}
        <p class="ref-peek-note muted">No description provided.</p>
      {/if}
    {/if}
    <div class="ref-peek-foot">
      {#if here}<a href={here} onclick={hide}>Open here</a>{/if}
      {#if shown.href}<a href={shown.href} target="_blank" rel="noreferrer">GitHub ↗</a>{/if}
    </div>
  </div>
{/if}

<style>
  /* The state beside each issue or pull request link in a rendered comment. */
  :global(.comment-body a[data-ref-state])::after {
    content: attr(data-ref-state);
    display: inline-block;
    margin-left: 4px;
    padding: 0 6px;
    border-radius: 30px;
    font-size: 10px;
    line-height: 1.6;
    vertical-align: 1px;
    text-decoration: none;
    background: var(--goodbg);
    color: var(--good);
  }
  :global(.comment-body a[data-ref-state='merged'])::after {
    background: var(--mergedbg);
    color: var(--merged);
  }
  :global(.comment-body a[data-ref-state='closed'])::after {
    background: var(--badbg);
    color: var(--bad);
  }
  :global(.comment-body a[data-ref-state='draft'])::after {
    background: var(--soft);
    color: var(--muted);
  }
  .ref-peek {
    position: fixed;
    z-index: 70;
    width: min(520px, calc(100vw - 32px));
    background: var(--surface);
    color: var(--fg);
    border: 1px solid var(--line);
    border-radius: 8px;
    box-shadow: 0 12px 32px #0000002e;
    font-size: 13px;
    overflow: hidden;
  }
  .ref-peek-head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 8px;
    padding: 10px 12px 4px;
  }
  .ref-peek-title {
    flex-basis: 100%;
    font-weight: 600;
    line-height: 1.35;
  }
  .ref-peek-state {
    font-size: 11px;
    line-height: 1.6;
    padding: 0 7px;
    border-radius: 30px;
    background: var(--goodbg);
    color: var(--good);
  }
  .ref-peek-state.merged {
    background: var(--mergedbg);
    color: var(--merged);
  }
  .ref-peek-state.closed {
    background: var(--badbg);
    color: var(--bad);
  }
  .ref-peek-state.draft {
    background: var(--soft);
    color: var(--muted);
  }
  .ref-peek-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 2px 12px;
    padding: 0 12px 8px;
    font-size: 12px;
  }
  .ref-peek-meta .add {
    color: var(--good);
    font-weight: 500;
  }
  .ref-peek-meta .del {
    color: var(--bad);
    font-weight: 500;
  }
  .ref-peek-body {
    max-height: 260px;
    overflow: auto;
    padding: 8px 12px;
    border-top: 1px solid var(--line);
    font-size: 12.5px;
  }
  .ref-peek-note {
    margin: 0;
    padding: 4px 12px 10px;
    font-size: 12px;
  }
  .ref-peek-foot {
    display: flex;
    gap: 14px;
    padding: 6px 12px;
    border-top: 1px solid var(--line);
    background: var(--code);
    font-size: 12px;
  }
</style>
